import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RiFilterLine, RiArrowUpDownLine, RiCloseLine, RiArrowDownLine, RiLoader4Line } from '@remixicon/react';
import { TailCard } from '@/features/tails';
import { Root as Button } from '@components/ui/Button';
import { useSearchStore } from '@/store/searchStore';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@store/authStore';
import TailDetailsDrawer from './components/TailDetailsDrawer';
import AddAircraftManuallyDrawer from '@/features/quotes/components/generate/AddAircraftManuallyDrawer';
import Modal from '@components/ui/Modal';
import SearchAircraft from '@/features/search/components/SearchAircraft';
import { TrustScoreDetailsContent } from '@/features/trust-score';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos, getEffectiveAircraft } from '@/features/tails/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@components/ui/DropdownMenu';
import useUserData from '../quotes/hooks/useUserData';
import { apiClient } from '@/lib/api';

const TRUST_SCORE_ORDER = { Pinnacle: 1, Premier: 2, Benchmark: 3, Standard: 4 };

const Tails = ({addtails = false, onClose}) => {
  const queryClient = useQueryClient();
  const { user, userProfile } = useAuthStore();
  const { data: userData } = useUserData(user?.id);
  const { searchTailNumbers, setSearchTailNumbers } = useSearchStore();
  const { addToShortlist, clearShortlist, clearSelectedTripGroup, shortlistAircraft, bids, setBids } = useAppStore();
  const [selectedTails, setSelectedTails] = useState(new Set());
  const [selectedAircraftMap, setSelectedAircraftMap] = useState(new Map());
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTail, setSelectedTail] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // When Tails renders inside the Quick Quote → Add Aircraft modal
  // (addtails=true), we can't let the TrustScore link route away —
  // GenerateQuote would unmount and the wizard state is only useState.
  // Instead we open the TrustScore details in a modal over the drawer.
  const [trustScoreAircraftId, setTrustScoreAircraftId] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [filters, setFilters] = useState({ trustScore: new Set(), status: new Set() });
  const TAILS_PAGE_SIZE = 9;
  const [visibleCount, setVisibleCount] = useState(TAILS_PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Transform searchTailNumbers to match the expected format
  const tails = useMemo(() => {
    if (!searchTailNumbers || !Array.isArray(searchTailNumbers) || searchTailNumbers.length === 0) {
      return [];
    }

    return searchTailNumbers.map(base => {
      const aircraft = getEffectiveAircraft(base);
      return {
        aircraftId: aircraft.aircraft_id,
        id: aircraft.id || aircraft._id || aircraft.tail_number,
        registration: aircraft.tail_number || '-',
        model: getAircraftModelName(aircraft),
        year: aircraft.mfr_year || '-',
        passengers: aircraft.passenger_capacity || aircraft.max_passengers,
        range: aircraft.aircraft_make_model?.min_range && aircraft.aircraft_make_model?.max_range ? `${aircraft.aircraft_make_model.min_range} - ${aircraft.aircraft_make_model.max_range} nm` : '-',
        operator: aircraft.operator,
        location: getOperatorWithLocation(aircraft.operator),
        trustScore: aircraft.operator?.rating_tiers || "Standard",
        trustScoreNum: aircraft.operator?.trust_score ?? null,
        status: aircraft.current_status || 'Unavailable',
        badges: aircraft.badges || [],
        image: getAircraftPhotos(aircraft, true),
        features: aircraft.aircraft_features,
        aircraft,
      };
    });
  }, [searchTailNumbers]);


  const toggleFilter = useCallback((group, value) => {
    setFilters(prev => {
      const newSet = new Set(prev[group]);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return { ...prev, [group]: newSet };
    });
  }, []);

  const hasActiveFilters = filters.trustScore.size > 0 || filters.status.size > 0;

  const clearFilters = useCallback(() => {
    setFilters({ trustScore: new Set(), status: new Set() });
  }, []);

  // Reset pagination when the underlying list, filters, or sort change.
  useEffect(() => {
    setVisibleCount(TAILS_PAGE_SIZE);
  }, [tails, filters, sortBy]);

  // Apply filters then sort
  const displayedTails = useMemo(() => {
    let result = [...tails];

    // Filter by trust score
    if (filters.trustScore.size > 0) {
      result = result.filter(t => filters.trustScore.has(t.trustScore));
    }
    // Filter by status
    if (filters.status.size > 0) {
      result = result.filter(t => filters.status.has(t.status));
    }

    // Sort
    if (sortBy === 'model') {
      result.sort((a, b) => (a.model || '').localeCompare(b.model || ''));
    } else if (sortBy === 'year') {
      result.sort((a, b) => {
        const ya = typeof a.year === 'number' ? a.year : 0;
        const yb = typeof b.year === 'number' ? b.year : 0;
        return yb - ya;
      });
    } else if (sortBy === 'passengers') {
      result.sort((a, b) => (b.passengers || 0) - (a.passengers || 0));
    } else if (sortBy === 'trustScore') {
      result.sort((a, b) => (TRUST_SCORE_ORDER[a.trustScore] || 99) - (TRUST_SCORE_ORDER[b.trustScore] || 99));
    }

    return result;
  }, [tails, filters, sortBy]);

  const handleSelectTail = (aircraftId, isSelected) => {
    setSelectedTails(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(aircraftId);
      } else {
        newSet.delete(aircraftId);
      }
      return newSet;
    });
    // Store aircraft data so it persists across searches. Key by
    // aircraft_id (stable UUID) — tail_number is a display string
    // and can collide across orgs / duplicate imports.
    if (isSelected) {
      const aircraft = searchTailNumbers.find(a => a.aircraft_id === aircraftId);
      if (aircraft) {
        setSelectedAircraftMap(prev => new Map(prev).set(aircraftId, aircraft));
      }
    } else {
      setSelectedAircraftMap(prev => {
        const next = new Map(prev);
        next.delete(aircraftId);
        return next;
      });
    }
  };

  const handleDetails = (aircraftId) => {
    const tail = searchTailNumbers.find(t => t.aircraft_id === aircraftId);
    if (tail) {
      setSelectedTail(tail);
      setDrawerOpen(true);
    } else {
      console.error('Tail not found for aircraft_id:', aircraftId);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTail(null);
  };

  // Called after the drawer's admin-only Delete Aircraft flow succeeds.
  // Prunes the deleted row from the local search list so it disappears
  // immediately without a re-search.
  const handleAircraftDeleted = (aircraftId) => {
    if (!aircraftId) return;
    setSearchTailNumbers(
      (searchTailNumbers || []).filter((a) => a.aircraft_id !== aircraftId),
    );
    // Drop it from any selection state too — a deleted tail can't be shortlisted.
    setSelectedTails((prev) => {
      const next = new Set(prev);
      next.delete(aircraftId);
      return next;
    });
    setSelectedAircraftMap((prev) => {
      const next = new Map(prev);
      next.delete(aircraftId);
      return next;
    });
  };

  // Edit-aircraft flow — opens the manual-add wizard in edit mode.
  // Details drawer is already closed by the button before this runs
  // (see TailDetailsDrawer's Edit button onClick).
  const [editAircraft, setEditAircraft] = useState(null);
  const handleAircraftEdit = (aircraft) => {
    if (!aircraft) return;
    setEditAircraft(aircraft);
  };

  // Called on successful save from the wizard's edit-mode chain. Splices
  // the updated aircraft into the local search list so the /tails card
  // reflects the edit immediately without a re-search. Details drawer
  // stays closed per spec — broker can re-open to double-check.
  const handleAircraftSaved = (updatedAircraft) => {
    if (!updatedAircraft?.aircraft_id) return;
    setSearchTailNumbers(
      (searchTailNumbers || []).map((a) =>
        a.aircraft_id === updatedAircraft.aircraft_id ? { ...a, ...updatedAircraft } : a,
      ),
    );
    setEditAircraft(null);
  };

  const handleAddSingleTail = async (aircraftId) => {
    const aircraftData = searchTailNumbers.find(aircraft => aircraft.aircraft_id === aircraftId);
    if (!aircraftData) {
      console.error('Aircraft not found for id:', aircraftId);
      return;
    }
    // Defensive: refuse to add an aircraft without an id. If this ever fires,
    // the upstream search/import path let an unpersisted record through and
    // the Quote Preview validation would reject the bid_option later.
    if (!aircraftData.aircraft_id) {
      toast.error('This aircraft is missing an id and can\'t be shortlisted. Please contact your admin.', { position: 'bottom-right' });
      return;
    }

    if (!addtails) {
      clearShortlist();
      clearSelectedTripGroup();

      const bidPayload = {
        trip_number: `T${Math.floor(Math.random() * 1000000)}`,
        bid_number: `Q${Date.now()}`,
        created_by: userData?.userprofile_id || "",
        broker_id: userData?.userprofile_id || "",
        broker_org_id: userProfile?.org_id || "",
        client_name: "",
        client_email: "",
        client_phone: "",
        departure_airport: "",
        arrival_airport: "",
        departure_date: new Date().toISOString(),
        return_date: new Date().toISOString(),
        passenger_count: 1,
        baggage_requirements: "",
        special_requests: "",
        status: "DRAFT",
        total_amount: 0,
        currency: "USD",
        valid_until: new Date().toISOString(),
        sent_at: new Date().toISOString(),
        viewed_at: new Date().toISOString(),
        accepted_at: new Date().toISOString(),
        notes: "",
        leg_itinerary: []
      };

      const createdBid = await apiClient.createBid(bidPayload);

      const createdOption = await apiClient.createBidOption({
        bid_id: createdBid.bid_id,
        aircraft_id: aircraftData.aircraft_id || "",
        operator_id: aircraftData.operator_id || "",
        base_price: "0.00",
        fuel_cost: "0.00",
        landing_fees: "0.00",
        crew_fees: "0.00",
        catering_cost: "0.00",
        ground_transport: "0.00",
        other_fees: "0.00",
        total_price: Number(aircraftData.total_price || 0).toFixed(2),
        broker_markup: Number(aircraftData.broker_markup || 0).toFixed(2),
        final_price: Number(aircraftData.final_price || 0).toFixed(2),
        federal_excise_tax: Number(aircraftData.federal_excise_tax || 0).toFixed(2),
        segment_tax: Number(aircraftData.segment_tax || 0).toFixed(2),
        platform_fee: Number(aircraftData.platform_fee || 0).toFixed(2),
        operator_cost: "0.00",
        is_recommended: true,
        recommendation_reason: "",
        availability_confirmed: true,
        shortlisted: true,
        risk_level: "LOW",
        trust_score: "0.00",
        estimated_flight_time: 0,
        aircraft_features: { beds: 1, wifi: false, entertainment: false },
      });

      const optimisticBid = {
        ...createdBid,
        bid_options: [{
          ...createdOption,
          shortlisted: true,
          aircraft: aircraftData,
          operator: aircraftData?.operator,
        }],
      };
      setBids([optimisticBid, ...(bids || [])]);
      queryClient.setQueriesData({ queryKey: ['shortlistedBids'] }, (old) => [optimisticBid, ...(old || [])]);
      toast.success('Added to shortlist', { position: 'bottom-right' });
      return;
    }

    addToShortlist(aircraftData);
    if (onClose) onClose();
  };

  const handleAddSelectedTails = async() => {

    // Clear selectedTripGroup before navigating to generate quote
    if(!addtails){
      setIsShortlisting(true);
      try {

      clearShortlist();
      clearSelectedTripGroup();

      // Resolve the aircraft sources FIRST — if none survive the
      // aircraft_id guard, bail before creating the bid so we don't
      // leave an orphan DRAFT /bids row in the DB.
      // Drop anything without an aircraft_id — that would create a
      // bid_option the Quote Preview validation rejects with "aircraft
      // is required".
      const optionSources = [...selectedTails]
        .map(aircraftId => selectedAircraftMap.get(aircraftId))
        .filter(source => !!source && !!source.aircraft_id);

      if (optionSources.length === 0) {
        toast.error("None of the selected aircraft could be shortlisted (missing id). Please contact your admin.", { position: 'bottom-right' });
        return;
      }

      // Transform quote data to bid creation format
      const bidPayload = {
        trip_number: `T${Math.floor(Math.random() * 1000000)}`,
        bid_number: `Q${Date.now()}`,
        created_by: userData?.userprofile_id || "",
        broker_id: userData?.userprofile_id || "",
        broker_org_id: userProfile?.org_id || "",
        client_name: "",
        client_email: "",
        client_phone: "",
        departure_airport: "",
        arrival_airport: "",
        departure_date: new Date().toISOString(),
        return_date: new Date().toISOString(),
        passenger_count: 1,
        baggage_requirements: "",
        special_requests: "",
        status: "DRAFT",
        total_amount: 0,
        currency: "USD",
        valid_until: new Date().toISOString(),
        sent_at: new Date().toISOString(),
        viewed_at: new Date().toISOString(),
        accepted_at: new Date().toISOString(),
        notes: "",
        leg_itinerary: []
      };

      const createdBid = await apiClient.createBid(bidPayload);

      const createdOptions = await Promise.all(
        optionSources.map(option => apiClient.createBidOption({
          bid_id: createdBid.bid_id,
          aircraft_id: option.aircraft_id || "",
          operator_id: option.operator_id || "",
          base_price: "0.00",
          fuel_cost: "0.00",
          landing_fees: "0.00",
          crew_fees: "0.00",
          catering_cost: "0.00",
          ground_transport: "0.00",
          other_fees: "0.00",
          total_price: Number(option.total_price || 0).toFixed(2),
          broker_markup: Number(option.broker_markup || 0).toFixed(2),
          final_price: Number(option.final_price || 0).toFixed(2),
          federal_excise_tax: Number(option.federal_excise_tax || 0).toFixed(2),
          segment_tax: Number(option.segment_tax || 0).toFixed(2),
          platform_fee: Number(option.platform_fee || 0).toFixed(2),
          operator_cost: "0.00",
          is_recommended: true,
          recommendation_reason: "",
          availability_confirmed: true,
          shortlisted: true,
          risk_level: "LOW",
          trust_score: "0.00",
          estimated_flight_time: 0,
          aircraft_features: { beds: 1, wifi: false, entertainment: false },
        }))
      );

      const optimisticBid = {
        ...createdBid,
        bid_options: createdOptions.map((opt, i) => ({
          ...opt,
          shortlisted: true,
          aircraft: optionSources[i],
          operator: optionSources[i]?.operator,
        })),
      };
      setBids([optimisticBid, ...(bids || [])]);
      queryClient.setQueriesData({ queryKey: ['shortlistedBids'] }, (old) => [optimisticBid, ...(old || [])]);
      setSelectedTails(new Set());
      setSelectedAircraftMap(new Map());
      toast.success('Added to shortlist', { position: 'bottom-right' });
      } catch (error) {
        console.error('Failed to shortlist tails:', error);
        toast.error('Failed to shortlist tails', { position: 'bottom-right' });
      } finally {
        setIsShortlisting(false);
      }
      return;

    }
    
    // Add all selected tails to shortlist. `selectedTails` now holds
    // aircraft_id (stable UUID), so look up the source record directly.
    selectedTails.forEach(aircraftId => {
      const aircraftData = searchTailNumbers.find(a => a.aircraft_id === aircraftId);
      if (aircraftData) {
        addToShortlist(aircraftData);
      }
    });

    onClose();
  };

  const handleOpenSearch = () => {
    setSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
  };

  return (
    <div className="py-2 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <div className="px-4 sm:px-6 lg:px-2 !bg-transparent max-h-[calc(100vh-150px)] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">
            {hasActiveFilters
              ? `${displayedTails.length} of ${tails.length} Tails`
              : `${tails.length} Tails Available`}
          </h1>
          <div className="flex items-center gap-3">
            {/* Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  mode="filled"
                  size="medium"
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 rounded-2xl py-2 !bg-white text-gray-500 !focus:outline-none"
                >
                  <RiFilterLine className="w-4 h-4" />
                  Filter
                  {hasActiveFilters && (
                    <span className="ml-1 w-5 h-5 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center">
                      {filters.trustScore.size + filters.status.size}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Trust Score</DropdownMenuLabel>
                {['Pinnacle', 'Premier', 'Benchmark', 'Standard'].map(tier => (
                  <DropdownMenuCheckboxItem
                    key={tier}
                    checked={filters.trustScore.has(tier)}
                    onCheckedChange={() => toggleFilter('trustScore', tier)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {tier}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                {['Available', 'Unavailable'].map(status => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={filters.status.has(status)}
                    onCheckedChange={() => toggleFilter('status', status)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
                {hasActiveFilters && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={false}
                      onCheckedChange={clearFilters}
                      className="text-red-600"
                    >
                      <RiCloseLine className="w-4 h-4 mr-1" />
                      Clear filters
                    </DropdownMenuCheckboxItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  mode="filled"
                  size="medium"
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 rounded-2xl py-2 !bg-white text-gray-500 !focus:outline-none"
                >
                  <RiArrowUpDownLine className="w-4 h-4" />
                  {sortBy ? `Sort: ${sortBy === 'trustScore' ? 'Trust' : sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}` : 'Sort by'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                  <DropdownMenuRadioItem value="model">Model (A-Z)</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="year">Year (Newest)</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="passengers">Passengers (Most)</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="trustScore">Trust Score (Highest)</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                {sortBy && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={false}
                      onCheckedChange={() => setSortBy('')}
                      className="text-red-600"
                    >
                      <RiCloseLine className="w-4 h-4 mr-1" />
                      Clear sort
                    </DropdownMenuCheckboxItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tails Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedTails.slice(0, visibleCount).map((tail) => (
            <TailCard
              key={tail.id}
              {...tail}
              isSelected={selectedTails.has(tail.aircraftId)}
              onSelect={handleSelectTail}
              onDetails={handleDetails}
              onShortlist={handleAddSingleTail}
              addtails={addtails}
            />
          ))}
        </div>

        {/* Load More */}
        {displayedTails.length > visibleCount && (
          <div className="flex justify-center mt-8">
            <button
              type="button"
              onClick={() => {
                setIsLoadingMore(true);
                // Defer the count bump so the spinner renders before the heavy grid re-mount.
                setTimeout(() => {
                  setVisibleCount((c) => c + TAILS_PAGE_SIZE);
                  setIsLoadingMore(false);
                }, 50);
              }}
              disabled={isLoadingMore}
              className={`group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm shadow-sm transition-all duration-200 ${
                isLoadingMore
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm'
              }`}
            >
              {isLoadingMore ? (
                <>
                  <RiLoader4Line className="w-4 h-4 animate-spin" />
                  <span>Loading more…</span>
                </>
              ) : (
                <>
                  <span>Load More</span>
                  <RiArrowDownLine className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Empty State */}
        {displayedTails.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">
              {tails.length === 0
                ? 'No tails available matching your criteria'
                : 'No tails match the current filters'}
            </div>
            <Button
              variant="primary"
              mode="filled"
              onClick={tails.length === 0 ? handleOpenSearch : clearFilters}
            >
              {tails.length === 0 ? 'Search Aircraft' : 'Clear Filters'}
            </Button>
          </div>
        )}

        {selectedTails.size > 0 && (
          <div className="fixed left-0 right-0 bottom-[calc(140px+env(safe-area-inset-bottom))] md:bottom-[70px] z-40 flex justify-center px-4 pointer-events-none">
            <Button
              variant="primary"
              mode="filled"
              size="large"
              onClick={handleAddSelectedTails}
              disabled={isShortlisting}
              className="shadow-lg rounded-full px-8 pointer-events-auto"
            >
              {isShortlisting
                ? 'Shortlisting...'
                : `${addtails ? 'Add' : 'Shortlist'} (${selectedTails.size}) Selected ${selectedTails.size > 1 ? 'Tails' : 'Tail'}`
              }
            </Button>
          </div>
        )}
      </div>

      {/* Tail Details Drawer */}
      <TailDetailsDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        tail={selectedTail}
        onShortlist={handleAddSingleTail}
        onDeleted={handleAircraftDeleted}
        onEdit={handleAircraftEdit}
        onOpenTrustScore={addtails ? setTrustScoreAircraftId : undefined}
      />

      {/* TrustScore Details modal — embedded flow only. Keeps the parent
          Quick Quote page mounted so wizard state survives (a route
          change to /tails/:id/trust-score would remount GenerateQuote
          and reset it back to the Client step). */}
      <Modal
        isOpen={!!trustScoreAircraftId}
        onClose={() => setTrustScoreAircraftId(null)}
        size="full"
        title="TrustScore"
        closeOnOutsideClick={false}
        // Stack above TailDetailsDrawer (which overrides its own z to 250).
        zClassName="z-[300]"
      >
        <div className="p-4 md:p-6">
          <TrustScoreDetailsContent aircraftId={trustScoreAircraftId} />
        </div>
      </Modal>

      {/* Edit-Aircraft wizard — same drawer the Quick Quote flow uses,
          but with initialAircraft prefilled which flips it to edit mode.
          Mounts only when editAircraft is set, so React unmounts on
          close and the wizard's own reset-on-close effect fires. */}
      <AddAircraftManuallyDrawer
        isOpen={!!editAircraft}
        onClose={() => setEditAircraft(null)}
        initialAircraft={editAircraft}
        onSaved={handleAircraftSaved}
      />

      {/* Search Aircraft Modal */}
      <Modal 
        isOpen={searchOpen} 
        onClose={handleCloseSearch}
        size="md"
        title="Search Aircraft"
        headerClassName="!border-0"
        closeOnOutsideClick={false}
      >
        <SearchAircraft showHeader={false} onClose={handleCloseSearch} />
      </Modal>
    </div>
  );
};

export default Tails;
